import * as THREE from 'three'
import { Text } from 'troika-three-text'

import MathsUtils from '@/utils/mathsUtils'

import BaseObject from './baseObject'
import EditingHandle from './editingHandle'
import GlassSegment from '@/model/glassSegment'

import GlassDesignerTypes from '../types'


// Editing Rectangle
// A rectangle used for creating new edge/corner cutouts
// Not currently used
export default class EditingRectangle extends BaseObject {
  constructor(editor, options = {}) {
    super(editor)

    this.rectHandleUserTag = 'rectHandle'

    this.lineColour = '#0085F3'
    this.shapeColour = '#606060'
    this.shapeOpacity = 0.5
    this.handleColour = '#FFFFFF'
    this.rotateHandleColour = '#00F385'

    this.position = new THREE.Vector2(0, 0)
    this.angle = 0
    this.dragAngle = this.angle
    this.width = 100
    this.height = 100

    this.rectMainGeom = null
    this.rectMainObj = null

    this.rectLineObj = []
    this.rectLineGeom = []
    this.rectHandleGrabPosition = new THREE.Vector2(0, 0)

    this.rectGrabPosition = new THREE.Vector2(0, 0)

    this.handleList = []
    this.rotateHandle = null
    this.rotateHandleGrabPosition = 0

    this.listenMouseEvents = true

    this.build()
  }

  // onMouseDownEvent
  onMouseDownEvent(ev, mouseData) {
    let raycaster = mouseData.isSnapGridEnabled ? mouseData.snapRaycaster : mouseData.raycaster
    if(MathsUtils.editingPlaneIntersection(raycaster.ray, this.rectGrabPosition)) {
      this.editor.captureEventObject(this)
    }
  }

  // onMouseMoveEvent
  onMouseMoveEvent(ev, mouseData) {
    // If captured, move the editing rectangle around
    if(this.editor.isCapturedEventObject(this)) {
      let raycaster = mouseData.isSnapGridEnabled ? mouseData.snapRaycaster : mouseData.raycaster

      // Move the editing rectangle around
      // Create a plane for the editing rectangle
      var intersectionPnt = new THREE.Vector3()
      if(MathsUtils.editingPlaneIntersection(raycaster.ray, intersectionPnt)) {

        // Move
        this.position.set(this.position.x + (intersectionPnt.x - this.rectGrabPosition.x), this.position.y + (intersectionPnt.y - this.rectGrabPosition.y))
        this.rectGrabPosition.set(intersectionPnt.x, intersectionPnt.y)

        // Notify any listener the rectangle has been moved
        this.sendObjectEvent(GlassDesignerTypes.ObjectEventTypes.objectMoved, {
          position: this.position
        }) 
      }
    } else {
      if(this.editor.isSelectEnabled())
        this.editor.setMouseCursorHand()
    }

    this.editor.flagRenderRequired()
  }

  // onMouseUpEvent
  onMouseUpEvent(ev, mouseData, intersectionInfo) {
    // If captured, release it & notify listener
    if(this.editor.isCapturedEventObject(this)) {

      this.sendObjectEvent(GlassDesignerTypes.ObjectEventTypes.objectReleased)

      this.editor.releaseCapturedObject()
    }
  }


  // onBeforeRender
  // Called prior to rendering of the object
  onBeforeRender() {
    super.onBeforeRender()

    var renderScale = 1.0 / this.editor.camera.zoom
    var lineWidth = renderScale * 1
    let halfWidth = this.width * 0.5
    let halfHeight = this.height * 0.5
    let rotateHandleOffsetX = (this.width * 0.5) + 15 * renderScale
    let rotateHandleOffsetY = (this.height * 0.5) + 15 * renderScale

    let angRads = MathsUtils.degreesToRadians(this.angle)
    let angUpRads = MathsUtils.degreesToRadians(MathsUtils.wrapAngleDegrees(this.angle - 90))

    let dir = MathsUtils.generate2DDirectionVector(angRads)
    let dirUp = MathsUtils.generate2DDirectionVector(angUpRads)

    // Update positions/scales/rotations
    let objAngles = new THREE.Euler(0, 0, angRads)

    this.rectMainObj.position.set(this.position.x, this.position.y, 90)
    this.rectMainObj.scale.set(this.width, this.height, 1)
    this.rectMainObj.setRotationFromEuler(objAngles)

    // Update the lines based on the rectangle position / size
    // Order is top, right, bottom, left
    this.rectLineObj[0].position.set(this.position.x + dirUp.x * halfHeight, this.position.y + dirUp.y * halfHeight, 90)
    this.rectLineObj[0].scale.set(this.width, lineWidth, 1)
    this.rectLineObj[0].setRotationFromEuler(objAngles)

    this.rectLineObj[1].position.set(this.position.x + dir.x * halfWidth, this.position.y + dir.y * halfWidth, 90)
    this.rectLineObj[1].scale.set(lineWidth, this.height, 1)
    this.rectLineObj[1].setRotationFromEuler(objAngles)

    this.rectLineObj[2].position.set(this.position.x - dirUp.x * halfHeight, this.position.y - dirUp.y * halfHeight, 90)
    this.rectLineObj[2].scale.set(this.width, lineWidth, 1)
    this.rectLineObj[2].setRotationFromEuler(objAngles) 

    this.rectLineObj[3].position.set(this.position.x - dir.x * halfWidth, this.position.y - dir.y * halfWidth, 90)
    this.rectLineObj[3].scale.set(lineWidth, this.height, 1)
    this.rectLineObj[3].setRotationFromEuler(objAngles)

    // Update drag handles
    // Order is top-left, top-right, bottom-right, bottom-left
    this.handleList[0].position.set(this.position.x + dirUp.x * halfHeight - dir.x * halfWidth, this.position.y + dirUp.y * halfHeight - dir.y * halfWidth, 99)
    this.handleList[1].position.set(this.position.x + dirUp.x * halfHeight + dir.x * halfWidth, this.position.y + dirUp.y * halfHeight + dir.y * halfWidth, 99)
    this.handleList[2].position.set(this.position.x - dirUp.x * halfHeight + dir.x * halfWidth, this.position.y - dirUp.y * halfHeight + dir.y * halfWidth, 99)
    this.handleList[3].position.set(this.position.x - dirUp.x * halfHeight - dir.x * halfWidth, this.position.y - dirUp.y * halfHeight - dir.y * halfWidth, 99)

    // Update rotation handle
    this.rotateHandle.position.set(this.position.x + rotateHandleOffsetX, this.position.y + rotateHandleOffsetY, 99)
  }


  /**
   * Build the initial drawing objects to display the editing line
   */
  build() {
    this.sceneObject = new THREE.Object3D()

    // Main rectangle shape
    this.rectMainGeom = new THREE.BoxGeometry(1, 1, 1)
    var material = new THREE.MeshBasicMaterial({
      color: this.shapeColour,
      transparent: true,
      opacity: this.shapeOpacity
    })
    this.rectMainObj = new THREE.Mesh(this.rectMainGeom, material)
    this.sceneObject.add(this.rectMainObj)

    // 4 Rectangle lines
    // Each corner having a handle
    for(var i=0; i<4; i++) {
      let edgeGeom = new THREE.BoxGeometry(1, 1, 1)
      material = new THREE.MeshBasicMaterial({
        color: this.lineColour
      })
      let edgeObj = new THREE.Mesh(edgeGeom, material)
      this.sceneObject.add(edgeObj)

      this.rectLineGeom.push(edgeGeom)
      this.rectLineObj.push(edgeObj)

      // Handles
      let handle = new EditingHandle(this.editor, { colour: this.handleColour })
      handle.userData = { type: this.rectHandleUserTag, index: i }
      handle.setObjectEventListener(this)
      this.handleList.push(handle)

      this.addChildObject(handle)
    }

    // Rotation handle
    // Disable grid snap on the rotate handle
    this.rotateHandle = new EditingHandle(this.editor, { colour: this.rotateHandleColour, outerRadius: 32, gridSnapDisabled: true })
    this.rotateHandle.setObjectEventListener(this)
    this.addChildObject(this.rotateHandle)
  }


  /**
   * Check for intersections between the object and a ray
   * Returns an object containing intersection point & object position
   * Returns null if no intersection occurred
   */
  checkRayIntersection(ray, worldRadius) {
    var rayInt = new THREE.Vector3()
    if(!MathsUtils.editingPlaneIntersection(ray, rayInt))
      return null

    let angRads = MathsUtils.degreesToRadians(this.angle)
    let angUpRads = MathsUtils.degreesToRadians(MathsUtils.wrapAngleDegrees(this.angle - 90))
    let dir = MathsUtils.generate2DDirectionVector(angRads)
    let dirUp = MathsUtils.generate2DDirectionVector(angUpRads)

    let refPlaneDirDistance = this.position.dot(dir)
    let refPlaneDirUpDistance = this.position.dot(dirUp)

    let grabDistanceX = Math.abs(dir.dot(rayInt) - refPlaneDirDistance)
    let grabDistanceY = Math.abs(dirUp.dot(rayInt) - refPlaneDirUpDistance)

    if(grabDistanceX < this.width*0.5 + worldRadius && grabDistanceY < this.height*0.5 + worldRadius) {
      return {
        intersection: rayInt,
        position: new THREE.Vector2(rayInt.x, rayInt.y),
        objectId: this.id,
        distance: Math.min(grabDistanceX, grabDistanceY)
      }
    }

    return null
  }


  /**
   * Receive an object event from objects we're listening too
   */
  onReceiveObjectEvent(object, eventType, data) {
    var renderScale = 1.0 / this.editor.camera.zoom
    let objUserData = object.userData || {}

    if(eventType == GlassDesignerTypes.ObjectEventTypes.objectCaptured) {
      if(object == this.rotateHandle) {
        this.rotateHandleGrabPosition = this.rotateHandle.position.x

        this.dragAngle = this.angle
      }

      if(objUserData.type == this.rectHandleUserTag) {
        this.rectHandleGrabPosition.set(object.position.x, object.position.y)
      }
    }
    if(eventType == GlassDesignerTypes.ObjectEventTypes.objectMoved) {
      // Rotate object
      if(object == this.rotateHandle) {
        let rotateFactor = 1.0

        let distance = this.rotateHandle.position.x - this.rotateHandleGrabPosition
        this.rotateHandleGrabPosition = this.rotateHandle.position.x

        this.dragAngle -= (distance * rotateFactor) / renderScale
        this.angle = MathsUtils.wrapAngleDegrees(this.dragAngle)

        // If snap enabled, snap the angle
        if(this.editor.getOptionSnapEnabled())
          this.angle = this.editor.calculateSnappedAngleDegrees(this.angle)

        this.sendObjectEvent(GlassDesignerTypes.ObjectEventTypes.objectUpdated)

        this.editor.flagRenderRequired()
      }

      // Update size
      if(objUserData.type == this.rectHandleUserTag) {

        // Resize
        let angRads = MathsUtils.degreesToRadians(this.angle)
        let angUpRads = MathsUtils.degreesToRadians(MathsUtils.wrapAngleDegrees(this.angle - 90))
        let dir = MathsUtils.generate2DDirectionVector(angRads)
        let dirUp = MathsUtils.generate2DDirectionVector(angUpRads)

        let refPlaneDirDistance = this.position.dot(dir)
        let refPlaneDirUpDistance = this.position.dot(dirUp)

        let grabOffsetX = dir.dot(this.rectHandleGrabPosition) - refPlaneDirDistance
        let grabOffsetY = dirUp.dot(this.rectHandleGrabPosition) - refPlaneDirUpDistance
        let grabMoveX = (dir.dot(object.position) - refPlaneDirDistance) - grabOffsetX
        let grabMoveY = (dirUp.dot(object.position) - refPlaneDirUpDistance) - grabOffsetY

        let oppositeHandleIndex = (objUserData.index + 2) % 4

        // Limit so the handles don't get dragged to opposite side
        if(Math.sign(grabOffsetX) == Math.sign(dir.dot(object.position) - refPlaneDirDistance)) {
          this.width = Math.abs(dir.dot(object.position) - this.handleList[oppositeHandleIndex].position.dot(dir))
          this.width = Math.max(this.width, 1)

          this.position.addScaledVector(dir, grabMoveX * 0.5)
          this.rectHandleGrabPosition.addScaledVector(dir, grabMoveX)
        }

        if(Math.sign(grabOffsetY) == Math.sign(dirUp.dot(object.position) - refPlaneDirUpDistance)) {
          this.height = Math.abs(dirUp.dot(object.position) - this.handleList[oppositeHandleIndex].position.dot(dirUp))
          this.height = Math.max(this.height, 1)

          this.position.addScaledVector(dirUp, grabMoveY * 0.5)
          this.rectHandleGrabPosition.addScaledVector(dirUp, grabMoveY)
        }

        this.sendObjectEvent(GlassDesignerTypes.ObjectEventTypes.objectUpdated)

        this.editor.flagRenderRequired()
      }
    }
  }


  /**
   * Calculate the segments used to represent the editing rectangle
   * Used for clipping operations
   */
  generateSegmentList() {
    var segmentList = []
    
    // Calculate from the 4 handles
    for(var i=0; i<4; i++) {
      let segment = new GlassSegment(GlassDesignerTypes.SegmentTypes.line)
      segment.startPosition.set(this.handleList[i].position.x, this.handleList[i].position.y)
      segment.endPosition.set(this.handleList[(i+1) % 4].position.x, this.handleList[(i+1) % 4].position.y)

      segmentList.push(segment)
    }

    return segmentList
  }

}
